import { ProviderUser } from "../types/ProviderUser";
import { GoogleAuth2Service } from "../../infrastructure/oauth2/GoogleOauth2Service";
import {
  updateProviderUser
} from "../../infrastructure/repositories/providerUserRepository";

export class GoogleEmailService {
  private static instance: GoogleEmailService;
  private googleAuthService: GoogleAuth2Service = GoogleAuth2Service.getInstance();

  /**
   * Private constructor for singleton pattern.
   * Intentionally empty as no initialization is needed.
   */
  private constructor() {
    // Private constructor for singleton pattern
  }

  public static getInstance(): GoogleEmailService {
    if (!GoogleEmailService.instance) {
      GoogleEmailService.instance = new GoogleEmailService();
    }
    return GoogleEmailService.instance;
  }

  /**
   * Checks if Gmail is connected for the provider user
   */
  public isGmailConnected(providerUser: ProviderUser): boolean {
    return providerUser.services?.gmail === true;
  }

  /**
   * Connects Gmail for the provider user
   */
  public async connectGmail(
    providerUser: ProviderUser,
    onSuccess?: () => void,
    onError?: (error: Error) => void
  ): Promise<void> {
    const BASE_SCOPES = [
      "https://www.googleapis.com/auth/userinfo.email",
      "https://www.googleapis.com/auth/userinfo.profile",
      "openid"
    ];
    const GMAIL_REQUIRED_SCOPES = [
      "https://www.googleapis.com/auth/gmail.readonly"
    ];
    const scopes = Array.from(new Set([
      ...BASE_SCOPES,
      ...GMAIL_REQUIRED_SCOPES
    ]));
    const options = {
      uxMode: "popup" as const,
      loginHint: providerUser.email || "",
      selectAccount: false,
      accessType: "offline",
      prompt: "consent",
    };
    const handleResponse = async (response: { code: string }) => {
      try {
        const googleAuthCode = response.code;
        console.log("[GoogleEmailService] Received auth code:", {
          codeLength: googleAuthCode.length,
          codePrefix: googleAuthCode.substring(0, 10) + '...',
          providerUserId: providerUser.uid
        });
        // Batch all updates in a single call
        await updateProviderUser(providerUser.uid, {
          googleAuthCode,
          services: { ...providerUser.services, gmail: true },
          scopes: { ...(providerUser.scopes || {}), gmail: scopes }
        });
        console.log("[GoogleEmailService] Provider user updated successfully");
        if (onSuccess) {
          onSuccess();
        }
      } catch (error: unknown) {
        console.error("[GoogleEmailService] Error in handleResponse:", {
          error,
          errorType: error instanceof Error ? error.constructor.name : typeof error,
          errorMessage: error instanceof Error ? error.message : String(error),
          providerUserId: providerUser.uid
        });
        if (onError) {
          onError(error instanceof Error ? error : new Error("Failed to connect Gmail"));
        }
      }
    };
    await this.googleAuthService.configureAuthClient(
      scopes,
      handleResponse,
      process.env.REACT_APP_GOOGLE_APP_CLIENT_ID || "",
      options,
    );
    this.googleAuthService.requestCode();
  }

  /**
   * Disconnects Gmail for the provider user
   */
  public async disconnectGmail(
    providerUser: ProviderUser,
    onSuccess?: () => void,
    onError?: (error: Error) => void
  ): Promise<void> {
    try {
      // Remove gmail from services and scopes
      const updatedServices = { ...providerUser.services, gmail: false };
      const updatedScopes = { ...(providerUser.scopes || {}) };
      delete updatedScopes.gmail;
      // Only clear tokens if no Google service is active
      const anyGoogleServiceActive = Object.entries(updatedServices)
        .filter(([key]) => key === "gmail" || key === "calendar")
        .some(([, active]) => active);
      const update: any = {
        services: updatedServices,
        scopes: updatedScopes,
      };
      if (!anyGoogleServiceActive) {
        update.tokens = null;
      }
      await updateProviderUser(providerUser.uid, update);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: unknown) {
      console.error("Error disconnecting Gmail:", error);
      if (onError) {
        onError(error instanceof Error ? error : new Error("Failed to disconnect Gmail"));
      }
    }
  }
}